import { Routes } from "@angular/router";
import { canActivateGuard } from "./auth-guard";
import { canActivateGuardSharing, canActivateChildGuardSharing } from "./auth-guard-sharing";
import { canActivateGuardOnlySSO } from "./auth-guard-onlySSO";
import { LicenseGuard } from "./licence-guard";
import { LICENSE_REQUIRED_PATH } from "./routes/route-paths";
import { featureFlagCanMatch } from "./services/featureFlag/featureFlag.guard";
import { FeatureFlags } from "./services/featureFlag/featureFlag.model";

import { BlankLayoutComponent } from "./views/shared/layouts/blankLayout.component";

const basicLayout = () => import("./views/shared/layouts/basicLayout.component").then((m) => m.BasicLayoutComponent);
const shareLayout = () => import("./views/shared/layouts/shareLayout.component").then((m) => m.ShareLayoutComponent);
const tripsLayout = () => import("./views/trips/layout/tripsLayout.component").then((m) => m.TripsLayoutComponent);

export const ROUTES: Routes = [
  // Main redirect
  {
    path: "",
    pathMatch: "full",
    loadComponent: () => import("./views/appviews/startupPageRedirect.component").then((m) => m.StartupPageRedirectComponent),
    canActivate: [canActivateGuard],
  },

  // Public routes (no auth required) — must be FIRST so Angular matches these
  // without loading any lazy feature modules
  {
    path: "",
    component: BlankLayoutComponent,
    children: [
      {
        path: "Login",
        loadComponent: () => import("./views/appviews/login.component").then((m) => m.LoginComponent),
        data: { title: "Login" },
      },
      {
        path: "Sharing/:token",
        loadComponent: () => import("./views/appviews/redirect.component").then((m) => m.RedirectComponent),
        data: { title: "Sharing" },
      },
      {
        path: "ForgotPassword",
        loadComponent: () => import("./views/appviews/forgotPassword.component").then((m) => m.ForgotComponent),
        data: { title: "ForgotPassword" },
        canActivate: [canActivateGuardOnlySSO],
      },
      {
        path: "SSOLogin",
        loadComponent: () => import("./views/appviews/sso-signin.component").then((m) => m.SsoSignInComponent),
        data: { title: "SSOLogin" },
      },
      {
        path: "Auth-callback",
        loadComponent: () => import("./views/appviews/sso-signin-callback.component").then((m) => m.SsoSignInCallbackComponent),
        data: { title: "SSO Callback" },
      },
      {
        path: "Unsubscribe/:id",
        loadComponent: () => import("./views/appviews/unsubscribe.component").then((m) => m.UnsubscribeComponent),
        data: { title: "Unsubscribe" },
      },
    ],
  },
  // Token-share view routes. Matched before the feature mounts and gated by the sharing-token guard
  // (not ordinary auth). Every surface reached via a `Sharing/:token` link resolves to one of these,
  // so they are grouped under the same `Sharing/` namespace as the token entry point. They are
  // internal navigation targets only — `RedirectComponent` reaches them with `skipLocationChange`, so
  // they never appear in an address bar, email, or bookmark and need no legacy redirect. Each is a
  // flat `:id` (a share token resolves to exactly one entity), and they are deliberately NOT nested as
  // `Devices/:id/Shared`: that path would live in this earlier-matched group and hijack an authed
  // user's real device sub-page.
  {
    path: "",
    loadComponent: shareLayout,
    canActivate: [canActivateGuardSharing],
    canActivateChild: [canActivateChildGuardSharing],
    children: [
      {
        path: "Sharing/Reports/:id",
        loadComponent: () => import("./views/reporting/reportingSharedDetails.component").then((m) => m.ReportingSharedDetailsViewComponent),
        data: { title: "Details Report" },
      },
      {
        path: "Sharing/Trips/:id",
        loadComponent: () => import("./views/devices/sharedTrips.component").then((m) => m.DeviceSharedTripsViewComponent),
        data: { title: "Trips Shared Tracker" },
      },
      {
        path: "Sharing/TripDetails/:id",
        loadComponent: () => import("./views/devices/sharedTripDetails.component").then((m) => m.DeviceSharedTripDetailsViewComponent),
        data: { title: "Trips Shared Tracker" },
      },
      {
        path: "Sharing/Devices/:id",
        loadComponent: () => import("./views/devices/deviceShareDetails.component").then((m) => m.DeviceShareDetailsViewComponent),
        data: { title: "Details Shared Tracker" },
      },
    ],
  },
  // App views — named path prefixes so Angular can skip non-matching route groups
  {
    path: "Home",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/home.routes").then((m) => m.HOME_ROUTES),
  },
  {
    path: "FleetOverview",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/overview.routes").then((m) => m.OVERVIEW_ROUTES),
  },
  {
    path: "Cockpit",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/cockpit.routes").then((m) => m.COCKPIT_ROUTES),
  },
  {
    path: "Dashboard",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/dashboard.routes").then((m) => m.DASHBOARD_ROUTES),
  },
  {
    path: "EcoDriving",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/ecodriving.routes").then((m) => m.ECODRIVING_ROUTES),
  },
  {
    path: "Equipment",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/equipment.routes").then((m) => m.EQUIPMENT_ROUTES),
  },
  {
    path: "Logistics",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/logistics.routes").then((m) => m.LOGISTICS_ROUTES),
  },
  {
    path: "CarSharing",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/carsharing.routes").then((m) => m.CARSHARING_ROUTES),
  },
  {
    path: "Drivers",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/drivers.routes").then((m) => m.DRIVERS_ROUTES),
  },
  {
    path: "DriverGroups",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/drivers.routes").then((m) => m.DRIVER_GROUPS_ROUTES),
  },
  {
    path: "AssetGroups",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/assets.routes").then((m) => m.ASSET_GROUPS_ROUTES),
  },
  {
    path: "Geofences",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/geofences.routes").then((m) => m.GEOFENCES_ROUTES),
  },
  {
    path: "GeofenceGroups",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/geofences.routes").then((m) => m.GEOFENCE_GROUPS_ROUTES),
  },
  {
    path: "Devices",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/devices.routes").then((m) => m.DEVICES_ROUTES),
  },
  {
    path: "Assets",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/assets.routes").then((m) => m.ASSETS_ROUTES),
  },
  {
    path: "Resellers",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/resellers.routes").then((m) => m.RESELLERS_ROUTES),
  },
  {
    path: "Users",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/users.routes").then((m) => m.USERS_ROUTES),
  },
  {
    path: "Accounts",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/accounts.routes").then((m) => m.ACCOUNTS_ROUTES),
  },
  {
    path: "System",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/system.routes").then((m) => m.SYSTEM_ROUTES),
  },
  {
    path: "Trailers",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/trailers.routes").then((m) => m.TRAILERS_ROUTES),
  },
  {
    path: "Projects",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/projects.routes").then((m) => m.PROJECTS_ROUTES),
  },
  {
    path: "Warehouses",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/warehouses.routes").then((m) => m.WAREHOUSES_ROUTES),
  },
  {
    path: "Fuel",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/fuel.routes").then((m) => m.FUEL_ROUTES),
  },
  {
    path: "Settings",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/settings.routes").then((m) => m.SETTINGS_ROUTES),
  },
  {
    path: "Notifications",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/notifications.routes").then((m) => m.NOTIFICATIONS_ROUTES),
  },
  {
    path: "Maintenances",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/maintenance.routes").then((m) => m.MAINTENANCE_ROUTES),
  },
  {
    // IOT-6210 prototype. canMatch, not canActivate, so an account without the flag never downloads
    // the chunk - the whole module is sample data and has no business reaching a customer.
    path: "TyrePressure",
    loadComponent: basicLayout,
    canMatch: [featureFlagCanMatch(FeatureFlags.TyrePressure)],
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/tyrepressure.routes").then((m) => m.TYREPRESSURE_ROUTES),
  },
  {
    path: "Reporting",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/reporting.routes").then((m) => m.REPORTING_ROUTES),
  },

  // ── Pre-migration URL prefixes ────────────────────────────────────────────
  // Detail pages used to live under `<Entity>Details`; they are now members of their collection
  // (`Devices/:id` rather than `DeviceDetails/:id`). These mounts only hold redirects, and exist
  // because customers have bookmarked these URLs and the WebserviceDispatcher has emailed them.
  // `LegacyRouteTelemetryService` beacons each pattern's first hit per session, so the whole block
  // can be deleted once nothing has hit it for a release or two.
  //
  // Like the other legacy prefixes, `DeviceDetails` now holds nothing but redirects, which resolve
  // during URL recognition before any layout is built — so it needs no layout or guards (the
  // canonical destination re-applies them). The token-share view moved to `Sharing/Devices/:id`
  // under ShareLayout above.
  {
    path: "DeviceDetails",
    loadChildren: () => import("./routes/devices.routes").then((m) => m.DEVICE_DETAILS_LEGACY_ROUTES),
  },
  {
    path: "AssetDetails",
    loadChildren: () => import("./routes/assets.routes").then((m) => m.ASSET_DETAILS_LEGACY_ROUTES),
  },
  {
    path: "DeviceSettingChangeDetails",
    loadChildren: () => import("./routes/devices.routes").then((m) => m.DEVICE_SETTING_CHANGE_LEGACY_ROUTES),
  },
  {
    path: "DriverDetails",
    loadChildren: () => import("./routes/drivers.routes").then((m) => m.DRIVER_DETAILS_LEGACY_ROUTES),
  },
  {
    path: "DriverGroupDetails",
    loadChildren: () => import("./routes/drivers.routes").then((m) => m.DRIVER_GROUP_DETAILS_LEGACY_ROUTES),
  },
  {
    path: "AssetGroupDetails",
    loadChildren: () => import("./routes/assets.routes").then((m) => m.ASSET_GROUP_DETAILS_LEGACY_ROUTES),
  },
  {
    path: "GeofenceDetails",
    loadChildren: () => import("./routes/geofences.routes").then((m) => m.GEOFENCE_DETAILS_LEGACY_ROUTES),
  },
  {
    path: "GeofenceGroupDetails",
    loadChildren: () => import("./routes/geofences.routes").then((m) => m.GEOFENCE_GROUP_DETAILS_LEGACY_ROUTES),
  },
  {
    path: "AccountDetails",
    loadChildren: () => import("./routes/accounts.routes").then((m) => m.ACCOUNT_DETAILS_LEGACY_ROUTES),
  },
  {
    path: "UserDetails",
    loadChildren: () => import("./routes/users.routes").then((m) => m.USER_DETAILS_LEGACY_ROUTES),
  },
  {
    path: "ResellerDetails",
    loadChildren: () => import("./routes/resellers.routes").then((m) => m.RESELLER_DETAILS_LEGACY_ROUTES),
  },
  {
    path: "ProjectDetails",
    loadChildren: () => import("./routes/projects.routes").then((m) => m.PROJECT_DETAILS_LEGACY_ROUTES),
  },
  {
    path: "TrailerDetails",
    loadChildren: () => import("./routes/trailers.routes").then((m) => m.TRAILER_DETAILS_LEGACY_ROUTES),
  },
  {
    path: "FuelDetails",
    loadChildren: () => import("./routes/fuel.routes").then((m) => m.FUEL_DETAILS_LEGACY_ROUTES),
  },
  {
    path: "WarehouseDetails",
    loadChildren: () => import("./routes/warehouses.routes").then((m) => m.WAREHOUSE_DETAILS_LEGACY_ROUTES),
  },
  {
    path: "WarehouseZoneDetails",
    loadChildren: () => import("./routes/warehouses.routes").then((m) => m.WAREHOUSE_ZONE_DETAILS_LEGACY_ROUTES),
  },
  {
    path: "ReportDetails",
    loadChildren: () => import("./routes/reporting.routes").then((m) => m.REPORT_DETAILS_LEGACY_ROUTES),
  },
  {
    path: "ReportSubscriptionDetails",
    loadChildren: () => import("./routes/reporting.routes").then((m) => m.REPORT_SUBSCRIPTION_DETAILS_LEGACY_ROUTES),
  },

  // Tags and system license — keep path: "" as these routes have their own full paths
  {
    path: "",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/system.routes").then((m) => m.TAGS_ROUTES),
  },
  {
    path: "",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard],
    loadChildren: () => import("./routes/system.routes").then((m) => m.SYSTEM_LICENSE_ROUTES),
  },

  // Standalone /Map alias (navigation links to /Map directly)
  {
    path: "Map",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    loadChildren: () => import("./routes/devices.routes").then((m) => m.MAP_ALIAS_ROUTE),
  },

  // Profile, account and sharing pages
  {
    path: "",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    children: [
      {
        path: "Profile",
        loadComponent: () => import("./views/appviews/profile.component").then((m) => m.ProfileComponent),
        data: { title: "Profile" },
      },
      {
        path: "MyAccount",
        loadComponent: () => import("./views/appviews/myAccount.component").then((m) => m.MyAccountComponent),
        data: { title: "MyAccount" },
      },
      {
        path: "Shared",
        loadComponent: () => import("./views/appviews/sharing.component").then((m) => m.SharingComponent),
        data: { title: "Shared" },
      },
      {
        path: "ShareTokens",
        loadComponent: () => import("./views/appviews/shareTokens.component").then((m) => m.ShareTokensComponent),
        data: { title: "ShareTokens" },
      },
      {
        path: "Tokens",
        loadComponent: () => import("./views/appviews/tokens.component").then((m) => m.TokenComponent),
        data: { title: "Tokens" },
      },
    ],
  },
  {
    path: "",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard],
    children: [
      {
        path: "Error404",
        loadComponent: () => import("./views/appviews/error404.component").then((m) => m.Error404Component),
        data: { title: "Error404" },
      },
    ],
  },
  {
    path: "",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard],
    children: [
      {
        path: "Search",
        loadComponent: () => import("./views/appviews/search.component").then((m) => m.SearchViewComponent),
        data: { title: "Search" },
      },
    ],
  },
  {
    path: "",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard],
    children: [
      {
        path: LICENSE_REQUIRED_PATH,
        loadComponent: () => import("./views/appviews/licenseRequired.component").then((m) => m.LicenseRequiredComponent),
        data: { title: "LicenseRequired" },
      },
    ],
  },
  {
    path: "",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard],
    children: [
      {
        path: "About",
        loadComponent: () => import("./views/appviews/about.component").then((m) => m.AboutViewComponent),
        data: { title: "About" },
      },
    ],
  },
  {
    path: "",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    children: [
      {
        path: "ChangeLog",
        loadComponent: () => import("./views/appviews/changelog.component").then((m) => m.ChangelogViewComponent),
        data: { title: "ChangeLog" },
      },
    ],
  },
  {
    path: "",
    loadComponent: basicLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    children: [
      {
        path: "Help",
        loadComponent: () => import("./views/appviews/help.component").then((m) => m.HelpViewComponent),
        data: { title: "Help" },
      },
    ],
  },
  // ── Trips Module (simplified fleet portal for end customers) ──────────────
  {
    path: "Trips",
    loadComponent: tripsLayout,
    canActivate: [canActivateGuard, LicenseGuard],
    children: [
      {
        path: "",
        loadChildren: () => import("./views/trips/trips.routes").then((m) => m.TRIPS_ROUTES),
      },
    ],
  },

  // Handle all other routes
  {
    path: "**",
    redirectTo: "Error404",
    data: { title: "Error 404" },
  },
];
